import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/BlogList.css';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaThumbsUp,
  FaComments,
  FaEye,
  FaClock,
  FaSearch,
  FaCheckCircle,
  FaClock as FaDraft,
  FaShare
} from 'react-icons/fa';
import SuperAdminSidebar from './SuperAdminSidebar';
import BloggerSidebar from '../Blogger/BloggerSidebar';
import CreateBloggerModal from './CreateBloggerModal';

const BlogList = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [showCreateBloggerModal, setShowCreateBloggerModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalBlogs: 0,
    limit: 10
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    // Get user from sessionStorage
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
    fetchBlogs();
  }, [location]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = blogs.filter(blog =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredBlogs(filtered);
    } else {
      setFilteredBlogs(blogs);
    }
  }, [searchTerm, blogs]);

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/my-blogs?page=${page}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setBlogs(response.data.data);
        setPagination(response.data.pagination);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load blogs');
      }
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      await axios.delete(
        `${API_BASE_URL}/api/blogs/${blogId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      setBlogs(blogs.filter(blog => blog._id !== blogId));
      setSuccessMessage('Blog deleted successfully!');
      setDeleteConfirm(null);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Failed to delete blog');
      console.error('Error deleting blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShareBlog = (blog) => {
    const blogUrl = `${window.location.origin}${user?.role === 'blogger' ? '/blogger/blogs/' : '/super-admin/blogs/'}${blog._id}`;
    const text = `Check out this blog: ${blog.title}`;
    
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: text,
        url: blogUrl
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(blogUrl);
      setSuccessMessage('Blog link copied to clipboard!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const getContentPreview = (content, length = 150) => {
    const text = stripHtml(content);
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  return (
    <div className="blog-list-container">
      {user?.role === 'blogger' ? <BloggerSidebar /> : <SuperAdminSidebar />}
      <div className="blog-list-wrapper">
        <div className="blog-list-header">
          <div className="header-content">
            <h1>My Blogs</h1>
            <p>{user?.role === 'blogger' ? 'View and manage your published blogs' : 'Manage and create your blog posts'}</p>
          </div>
         
          <div className="header-buttons">
            <button
              className="btn-create-blog"
              onClick={() => navigate(user?.role === 'blogger' ? '/blogger/blogs/create' : '/super-admin/blogs/create')}
            >
              <FaPlus /> Create New Blog
            </button>
            {user?.role !== 'blogger' && (
              <button
                className="btn-create-blogger"
                onClick={() => setShowCreateBloggerModal(true)}
              >
                <FaPlus /> Create Blogger 
              </button>
            )}
          </div>
        </div>

      {successMessage && (
        <div className="success-alert">
          <FaCheckCircle /> {successMessage}
        </div>
      )}

      {error && (
        <div className="error-alert">
          {error}
        </div>
      )}

      <div className="blog-search-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search blogs by title, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        {blogs.length > 0 && (
          <div className="blog-stats">
            <span className="stat-item">
              Total Blogs: <strong>{pagination.totalBlogs}</strong>
            </span>
          </div>
        )}
      </div>

      {loading && !blogs.length ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading blogs...</p>
        </div>
      ) : filteredBlogs.length > 0 ? (
        <>
          <div className="blogs-grid">
            {filteredBlogs.map(blog => (
              <div key={blog._id} className="blog-card-wrapper">
                <div 
                  className="blog-card"
                  onClick={() => navigate(user?.role === 'blogger' ? `/blogger/blogs/${blog._id}` : `/super-admin/blogs/${blog._id}`)}
                >
                  {blog.featuredImage && (
                    <div className="blog-featured-image">
                      <img src={blog.featuredImage} alt={blog.title} />
                    </div>
                  )}

                  <div className="blog-card-content">
                    <div className="blog-header-info">
                      <div className="blog-title-section">
                        <h3 className="blog-title">{blog.title}</h3>
                        <span className={`status-badge ${blog.status}`}>
                          {blog.status === 'published' ? <FaCheckCircle /> : <FaDraft />}
                          {blog.status}
                        </span>
                      </div>
                      <p className="blog-date">
                        <FaClock /> {formatDate(blog.createdAt)}
                      </p>
                    </div>

                    {blog.category && (
                      <span className="blog-category">{blog.category}</span>
                    )}

                    {blog.description && (
                      <p className="blog-description">{blog.description}</p>
                    )}

                    <p className="blog-preview">{getContentPreview(blog.content)}</p>

                    {blog.tags && blog.tags.length > 0 && (
                      <div className="blog-tags">
                        {blog.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="tag">
                            #{tag}
                          </span>
                        ))}
                        {blog.tags.length > 3 && (
                          <span className="tag-more">+{blog.tags.length - 3}</span>
                        )}
                      </div>
                    )}

                    <div className="blog-stats-row">
                      <div className="stat">
                        <FaThumbsUp /> {blog.likes}
                      </div>
                      <div className="stat">
                        <FaComments /> {blog.comments.length}
                      </div>
                      <div className="stat">
                        <FaEye /> {blog.views}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="blog-action-buttons">
                  <button
                    className="btn-action btn-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(user?.role === 'blogger' ? `/blogger/blogs/edit/${blog._id}` : `/super-admin/blogs/edit/${blog._id}`);
                    }}
                    title="Edit Blog"
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    className="btn-action btn-share"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShareBlog(blog);
                    }}
                    title="Share Blog"
                  >
                    <FaShare /> Share
                  </button>
                  <button
                    className="btn-action btn-delete"
                    onClick={() => setDeleteConfirm(deleteConfirm === blog._id ? null : blog._id)}
                    title="Delete Blog"
                  >
                    <FaTrash /> {deleteConfirm === blog._id ? 'Confirm?' : 'Delete'}
                  </button>
                  {deleteConfirm === blog._id && (
                    <button
                      className="btn-action btn-confirm-delete"
                      onClick={() => handleDeleteBlog(blog._id)}
                      disabled={loading}
                    >
                      Yes, Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => fetchBlogs(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchBlogs(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h2>No Blogs Found</h2>
          <p>
            {searchTerm
              ? 'No blogs match your search. Try different keywords.'
              : "You haven't created any blogs yet. Start creating one!"}
          </p>
          <button
            className="btn-create-blog"
            onClick={() => navigate(user?.role === 'blogger' ? '/blogger/blogs/create' : '/super-admin/blogs/create')}
          >
            <FaPlus style={{ fontSize: '1rem', marginTop: '14px' }} /> Create Your First Blog
          </button>
        </div>
      )}
      
      {user?.role !== 'blogger' && (
        <CreateBloggerModal 
          isOpen={showCreateBloggerModal}
          onClose={() => setShowCreateBloggerModal(false)}
          onSuccess={() => {
            setShowCreateBloggerModal(false);
            setSuccessMessage('Blogger created successfully!');
            setTimeout(() => setSuccessMessage(''), 5000);
          }}
        />
      )}
      </div>
    </div>
  );
};

export default BlogList;
